import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { LayerType, TextWritingType, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { FontStyleGroup, type Features } from '@/components/font-style-group';
import { resolveFontStyleValues } from '@/components/font-style-group/resolve-values';
import { useEditor } from '@/hooks/use-editor';
import { useListenActivePickLayerProps } from '@/hooks/use-listen-layer-props';
import { useTextRangeAttrsState } from '@/hooks/use-text-range-attrs-state';
import { useLocaleStore } from '@/i18n';
import { checkIsInArtTextGeneratorMode } from '@/utils/mode';
import { useHighlight, useLayerProps } from '../../hooks';
import { type WidgetCommonProps } from '../../types';
import { Tracker } from '../../utils';

type ListenedProps = Pick<
    ITextLayerProps,
    | 'bold'
    | 'italic'
    | 'lineThrough'
    | 'underline'
    | 'writingMode'
    | 'texture'
    | 'textGradient'
    | 'textContents'
    | 'advancedTextMaterial'
>;

type Props = WidgetCommonProps & {
    closedFeatures?: Features[];
};

/**
 * 在文字属性面板展示并修改选中图层的样式，依赖图层选中状态与文本编辑选区
 *
 * @param props - 目标图层、埋点记录与需要关闭的样式项
 * @returns 文字样式控件；操作会更新图层并记录历史与埋点
 */
export const FontStyle: React.FC<Props> = ({ layerId, recordId, closedFeatures }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const textRangeAttrsState = useTextRangeAttrsState(layerId);
    const props = useLayerProps(editor, (layer) => {
        if (!layer) {
            return {
                bold: false,
                italic: false,
                lineThrough: false,
                underline: false,
                writingMode: TextWritingType.HORIZONTAL_TB,
                texture: null,
                textGradient: null,
                textContents: [],
                advancedTextMaterial: null,
            };
        }

        const layerData = layer.data as ITextLayerProps;
        const {
            bold,
            italic,
            lineThrough,
            underline,
            writingMode,
            texture,
            textGradient,
            textContents,
            advancedTextMaterial,
        } = layerData;

        return {
            bold,
            italic,
            lineThrough,
            underline,
            writingMode,
            texture,
            textGradient,
            textContents,
            advancedTextMaterial,
        };
    }) as ListenedProps;

    const { hasNoHighlight } = useHighlight(editor);

    /**
     * 竖排切换的准入判定，不能复用上方 `useLayerProps` 的返回值。
     *
     * `useLayerProps` 在选中组图层时只会通过 `findFirstTextLayer` 取第一个文本子层，
     * 再从该层读取 props。这适合 bold/writingMode 等「展示当前值」的场景，但不足以判断
     * 「整组是否允许切换竖排」。
     *
     * 竖排文字要求：当前操作涉及的每个文本层都不能是划重点、花字或变形文字。
     * 组操作会遍历全部一级直接子图层；只要其中任一子层不满足，整次切换会被拒绝且无数据变化。
     *
     * 因此 UI 必须按实际操作范围做聚合：组内选中子层时只检查该子层，选中整组时检查直接子层。
     * - 单层：只检查该文本层本身；
     * - 组层：检查全部一级直接子图层（与 core 边界一致，不递归嵌套组）。
     *
     * 若只信 `useLayerProps` / `hasNoHighlight`（同样只覆盖 pick/首个子层），会出现
     * 「首个子层可切竖排、后续子层有变形文字/艺术字/高亮 → 按钮仍可点，但 core 拒绝」
     * 的 UI/引擎不一致。也不应为此扩大共享 `useLayerProps` 的语义。
     */
    const writingModeEligible = useListenActivePickLayerProps(
        editor,
        (pickLayer) => {
            const layer = pickLayer ?? editor.services.frame.getActiveLayer();
            const layers = layer?.layerType === LayerType.GROUP_LAYER ? layer.children : layer ? [layer] : [];
            return (
                layers.length > 0 &&
                layers.every((item) => {
                    if (item.layerType !== LayerType.TEXT_LAYER) return false;
                    const data = item.data as ITextLayerProps;
                    const hasHighlight = !!data.textMark || data.textContents?.some((content) => !!content.textMark);
                    return !hasHighlight && !data.advancedTextMaterial && !data.shapeExtension;
                })
            );
        },
        // 不按初始图层类型过滤事件，保证子层与整组之间切换时仍更新准入状态。
        false,
    );

    const { bold, italic, lineThrough, underline } = resolveFontStyleValues(props, textRangeAttrsState);

    const writingMode = props.writingMode;
    const texture = props.texture;
    const textGradient = props.textGradient;
    const advancedTextMaterial = props.advancedTextMaterial;

    const isUsingTexture = !!texture && !!texture.url;
    const isUsingGradient = !!textGradient && textGradient.colors.length > 1;
    const isInArtTextGeneratorMode = checkIsInArtTextGeneratorMode();

    const disableWritingMode = (() => {
        if (!hasNoHighlight) return $t((v) => v.textMarkSelector.disabledByTextMark);
        if (advancedTextMaterial || !writingModeEligible) return true;
        return false;
    })();
    const disableUnderline = isUsingTexture || isUsingGradient;
    const disableLineThrough = isUsingTexture || isUsingGradient;

    const handleChange = (kind: Features, value: boolean | string) => {
        switch (kind) {
            case 'bold':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance<TextLayer>('TextLayer')
                        .adjustArtTextSingleCharacter(layerId, { bold: value as boolean }, { writeHistory: true });
                }
                editor.getInstance<TextLayer>('TextLayer').setBoldStyle(layerId, value as boolean);
                break;
            case 'italic':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance<TextLayer>('TextLayer')
                        .adjustArtTextSingleCharacter(layerId, { italic: value as boolean }, { writeHistory: true });
                }
                editor.getInstance<TextLayer>('TextLayer').setItalicStyle(layerId, value as boolean);
                break;
            case 'lineThrough':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance<TextLayer>('TextLayer')
                        .adjustArtTextSingleCharacter(
                            layerId,
                            { lineThrough: value as boolean },
                            { writeHistory: true },
                        );
                }
                editor.getInstance<TextLayer>('TextLayer').setLineThrough(layerId, value as boolean);
                break;
            case 'underline':
                if (isInArtTextGeneratorMode) {
                    return editor
                        .getInstance<TextLayer>('TextLayer')
                        .adjustArtTextSingleCharacter(layerId, { underline: value as boolean }, { writeHistory: true });
                }
                editor.getInstance<TextLayer>('TextLayer').setUnderline(layerId, value as boolean);
                break;
            case 'writingMode':
                editor.getInstance<TextLayer>('TextLayer').setWritingMode(layerId, value as TextWritingType);
                break;
        }

        Tracker.setFontStyle({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            type: kind,
            value,
        });
    };

    return (
        <FontStyleGroup
            closedFeatures={closedFeatures}
            disableWritingMode={disableWritingMode}
            disableLineThrough={disableLineThrough}
            disableUnderline={disableUnderline}
            bold={bold}
            italic={italic}
            lineThrough={lineThrough}
            underline={underline}
            writingMode={writingMode}
            onChange={handleChange}
        />
    );
};
